import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { CommitOnEnterDirective } from 'src/app/shared/directives/commit-on-enter.directive';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';
import { ConfirmationModalComponent } from 'src/app/shared/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalData } from 'src/app/shared/modals/confirmation-modal/modal-data.models';
import { ReallocationV2Store } from '../../state/reallocation-v2.store';

/**
 * Headings for the SMA dialogs.
 *
 * Two headings over three uses. The confirm heading covers moving INTO an SMA and removing one
 * -- both are questions the advisor answers, and both are the same fact about the account, so
 * the same heading is right even though the copy differs.
 *
 * The blocked heading is separate because that dialog does a different job: it reports that the
 * account cannot go where the advisor pointed it. A single neutral heading over a refusal reads
 * as though a choice is still on offer.
 */
const SMA_CONFIRM_TITLE = 'Confirm Separately Managed Account';

/**
 * Heading for the locked-down notice, which is neither a confirmation nor a refusal of something
 * the advisor picked -- it states what the account IS and what has to happen first.
 */
const SMA_LOCKED_TITLE = 'This account is a Separately Managed Account';
const SMA_BLOCKED_TITLE = 'This account can\'t use this model';

/**
 * Step 4 — split the amount going to models across them.
 *
 * Inputs are bound one-way from the store's derived `allocation()` view and push edits back
 * through store methods, so the store stays the single source of truth.
 *
 * ⚠️ **One box, one row (ACMTECH-969).** Nothing here rebalances siblings any more. The advisor
 * states the total going to models -- {@link ReallocationV2Store.modelPot} -- and each row's
 * dollars are `pot × target%`; an edit to a Target %, a Value or a split changes that row and
 * nothing else. The three percentage columns must still each total 100 to leave the step, but
 * getting them there is the advisor's, not the wizard's, which is what stopped one keystroke
 * from renumbering figures nobody had looked at.
 */
@Component({
  selector: 'app-rav2-model-allocation-step',
  standalone: true,
  imports: [CommonModule, SelectOnFocusDirective, CommitOnEnterDirective, TwoDecimalsDirective],
  templateUrl: './model-allocation-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelAllocationStepComponent {
  readonly store = inject(ReallocationV2Store);
  private readonly dialog = inject(MatDialog);

  fmtPct(value: number): string {
    return value.toFixed(2);
  }

  /**
   * A percentage for an INPUT box — blank at zero.
   *
   * Since ACMTECH-945 the grid opens with nothing allocated, so every percentage derives from
   * values that are not there yet and reads 0. Showing "0.00" in seven boxes makes an untouched
   * grid look filled in and wrong; blank makes it look like what it is, waiting for input.
   *
   * Distinct from {@link fmtPct}, which still formats the TOTALS row — a total of zero is a
   * real figure and belongs on screen.
   */
  pctInput(value: number): string {
    return value === 0 ? '' : value.toFixed(2);
  }

  /**
   * A dollar figure for a money input: grouped to thousands and held to two decimals.
   *
   * `203280.61` -> `203,280.61`. The separators are what make a six-figure pot scannable; the
   * boxes they go in are `type="text" inputmode="decimal"` precisely so they can carry one (a
   * `type="number"` input's value sanitiser rejects any string that is not a plain float, so
   * assigning a grouped figure there EMPTIES the box).
   *
   * ⚠️ Every parse of these boxes must strip the separators first, or `parseFloat` stops at the
   * comma and reads `203,280.61` as 203. {@link onPot} and {@link onValue} both do.
   *
   * ⚠️ Deliberately NOT the `currency` pipe. The `$` sits outside the box on every money input
   * in this wizard, and a symbol inside the field would have to be parsed back out of anything
   * the advisor typed.
   */
  private money(value: number): string {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * An allocated value for its input box — blank at zero.
   *
   * A zero row is never valid (the advisor funds it or removes it), so there is no state this
   * hides: blank and zero mean the same thing here, and blank is the honest rendering of a box
   * nobody has typed in.
   *
   * ⚠️ The figure it shows is DERIVED (`pot × target%`), so a typed amount comes back rounded
   * to the cent and, when the pot is small enough for a hundredth to matter, a cent off what
   * was typed. That is the pot's arithmetic being honest rather than the box remembering an
   * amount that no longer follows from anything.
   */
  valueInput(value: number): string {
    return value === 0 ? '' : this.money(value);
  }

  private num(event: Event): number {
    return parseFloat((event.target as HTMLInputElement).value.replace(/,/g, '')) || 0;
  }

  /**
   * Commit a model selection — after the SMA questions, when there are any.
   *
   * Intercepted in the COMPONENT rather than in `selectModel`, because confirm-then-commit
   * is asynchronous and the store is synchronous and has no MatDialog. The store still owns
   * the decision and the copy ({@link ReallocationV2Store.smaSelectionCheck}); this owns
   * only the dialog and the revert.
   *
   * This is the single funnel for the dropdown, so there is no second path an SMA could be
   * chosen through.
   */
  onModel(id: string, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const modelId = select.value;
    const check = this.store.smaSelectionCheck(modelId);

    if (check.kind === 'none') {
      this.store.selectModel(id, modelId);
      return;
    }

    if (check.kind === 'blocked') {
      this.revertSelection(select, id);
      // No Cancel, and no warning red: there is nothing to decide and nothing has gone
      // wrong -- the selection simply did not take, and this says why.
      this.openSmaDialog({
        title: SMA_BLOCKED_TITLE,
        message: check.message,
        hideCancelButton: true,
        confirmationButtonText: 'OK',
      });
      return;
    }

    this.openSmaDialog({
      title: SMA_CONFIRM_TITLE,
      message: check.message,
      question: check.question,
      confirmationButtonText: 'Confirm',
    })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) this.store.commitToSma(modelId);
        else this.revertSelection(select, id);
      });
  }

  /**
   * Add a model row -- unless an SMA already holds the account, in which case say so.
   *
   * ⚠️ The button is dimmed by CLASS, not by the `disabled` attribute, so the click still reaches
   * this method. That is the point: a truly disabled control cannot explain itself, and "the
   * button is grey" is not an answer to "why can I not add a model". Same convention the account
   * request modal's nav buttons use, and like them this re-checks the condition rather than
   * trusting the styling.
   */
  onAddModel(): void {
    if (this.store.smaLockActive()) {
      this.openSmaDialog({
        title: SMA_LOCKED_TITLE,
        message: this.store.smaLockMessage(),
        hideCancelButton: true,
        confirmationButtonText: 'OK',
      });
      return;
    }

    this.store.addRow();
  }

  /**
   * Drop a model row -- after confirmation when the row holds an SMA.
   *
   * Intercepted in the COMPONENT for the same reason `onModel` is: confirm-then-act is
   * asynchronous, the store is synchronous and has no MatDialog. The store owns the decision
   * and the copy ({@link ReallocationV2Store.smaRemovalPrompt}); this owns only the dialog.
   *
   * 🟢 No revert branch, unlike `onModel`. Nothing has changed yet when the question is asked
   * -- the click does not remove the row, this does -- so cancelling means doing nothing rather
   * than undoing something.
   */
  onRemove(rowId: string): void {
    const prompt = this.store.smaRemovalPrompt(rowId);

    if (prompt === null) {
      this.store.removeRow(rowId);
      return;
    }

    this.openSmaDialog({ title: SMA_CONFIRM_TITLE, ...prompt, confirmationButtonText: 'Confirm' })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) this.store.removeRow(rowId);
      });
  }

  /**
   * Open one of the SMA dialogs at a readable width.
   *
   * ⚠️ The width is not decoration. `mat-dialog-container` has no width of its own — only
   * `max-w-full` — so the panel sizes to its content, and a paragraph's max-content width is
   * the whole unwrapped paragraph. Every other confirmation in the portal is a single short
   * sentence, so this never showed up; these messages run to a few hundred characters and
   * stretched the panel across the viewport.
   *
   * 56rem is 560px against this app's 10px root, which is roughly 60 characters a line at
   * `text-lg` (16px) — a normal measure to read. `maxWidth` keeps it inside a narrow screen,
   * where 560px would otherwise overflow.
   */
  private openSmaDialog(data: ConfirmationModalData) {
    return this.dialog.open<ConfirmationModalComponent, ConfirmationModalData, boolean>(
      ConfirmationModalComponent,
      { width: '56rem', maxWidth: '90vw', data },
    );
  }

  /**
   * Put the dropdown back on the row's actual model.
   *
   * ⚠️ Explicitly, on the element. The `<option [selected]>` bindings read `row.modelId`,
   * which never changed — the browser moved the select on its own — so leaving the store
   * untouched leaves the DOM showing a model the row does not hold. Nothing in Angular
   * re-runs to correct that, because from its side nothing happened.
   */
  private revertSelection(select: HTMLSelectElement, rowId: string): void {
    select.value = this.store.allocation().rows.find((r) => r.id === rowId)?.modelId ?? '';
  }

  onTarget(id: string, event: Event): void {
    this.store.editTarget(id, this.num(event));
  }

  onDistribution(id: string, event: Event): void {
    this.store.editDistribution(id, this.num(event));
  }

  onContribution(id: string, event: Event): void {
    this.store.editContribution(id, this.num(event));
  }

  onValue(id: string, event: Event): void {
    const raw = (event.target as HTMLInputElement).value.replace(/,/g, '');
    const parsed = parseFloat(raw);
    // Emptying the box means NOTHING allocated to this row -- not "revert to the
    // percentage-derived figure", which is what clearing the cap used to do.
    //
    // That fallback made sense while the column was an override on top of a derived value.
    // Since ACMTECH-944 the grid OPENS at what each sleeve holds, so falling back reverted
    // the row to absorbing the whole investable amount: the box read empty, the total read
    // the full figure, and Next stayed enabled. Zero is the honest reading of an empty box,
    // and it leaves the money unapplied so the gate can catch it.
    this.store.editValue(id, raw === '' || isNaN(parsed) ? 0 : parsed);
  }

  /**
   * Commit a typed pot. Blank means nothing to models, which is a legitimate request.
   *
   * Parsed here rather than through {@link num} because that helper's `|| 0` would turn a
   * mistyped figure into zero silently; here zero IS the reading of an empty box, and the store
   * clamps the rest.
   */
  onPot(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/,/g, '');
    const parsed = parseFloat(raw);
    this.store.setModelPot(raw === '' || isNaN(parsed) ? 0 : parsed);

    // ⚠️ WRITE THE ACCEPTED FIGURE BACK, explicitly, on the element.
    //
    // `setModelPot` clamps to what the account has to invest, so a figure above the ceiling was
    // already refused -- but the BOX went on showing it. `[value]` is bound to `potInput()`, and
    // when the clamp lands on the figure the pot already held, that expression returns the same
    // string it returned last time: from Angular's side nothing changed, so nothing re-renders
    // and the advisor is left looking at a number the request does not hold. Type 999,999 into a
    // field already at its ceiling and the two disagree indefinitely.
    //
    // Same failure and the same fix as {@link revertSelection} on the model dropdown, which
    // documents it for the `<select>` case: the browser moved the control on its own, so only an
    // explicit write puts it back.
    input.value = this.potInput();
  }

  /** The pot for its input box. Never blank: zero to models is a figure, not an absence. */
  potInput(): string {
    return this.money(this.store.modelPot());
  }

  /**
   * The same pot as a percentage OF THE WHOLE ACCOUNT, for the box beside it.
   *
   * ⚠️ DERIVED, never stored. The dollars are the request; this is a second way to type them.
   * Holding a percentage of its own would give the step two sources for one figure, and they
   * would disagree the moment anything else moved the pot — Allocate Remaining, a sale on step 1,
   * an SMA commit. Reading through on every render costs a division and cannot go stale.
   *
   * ⚠️ AGAINST `totalValue`, the account, not `investable`. That is what the advisor is thinking
   * in — "put a third of this account into models" — and it is what the strip's own figures are
   * shares of.
   *
   * 🟢 The full 0–100 range is reachable, because the pot is no longer clamped to what is
   * currently investable (AndyP, 2026-09-22, from advisor feedback). Asking for more than the
   * account has freed up is accepted and shows as a deficit, which is what sends the advisor back
   * to step 1 to sell. A percentage that could only reach some fraction of its own scale would
   * have been a poor control for exactly the request that prompted it.
   */
  potPercentInput(): string {
    const total = this.store.totalValue();
    if (total <= 0) return '0.00';
    return ((this.store.modelPot() / total) * 100).toFixed(2);
  }

  /**
   * Set the pot from a percentage of the account.
   *
   * ⚠️ ONE-WAY PER EDIT, which is the whole trick of a two-box control. This writes dollars; the
   * percentage box is re-read from those dollars. What it must NOT do is the reverse as well —
   * rewriting the dollars from the rounded percentage would quietly change a figure the advisor
   * typed exactly. $156,615.79 of $557,842.62 shows as 28.08%, and 28.08% of the account is
   * $156,642.21: a $26 drift, appearing from nowhere, for touching nothing.
   *
   * So the percentage is only ever an INPUT when it is the box being typed in, and only ever an
   * OUTPUT otherwise.
   */
  onPotPercent(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/[,%]/g, '');
    const parsed = parseFloat(raw);
    const pct = raw === '' || isNaN(parsed) ? 0 : Math.max(0, parsed);

    // Rounded to the cent before it is stored: a percentage of an odd account total lands on
    // fractions of a cent, and the pot is money the rest of the step divides up.
    const dollars = Math.round((pct / 100) * this.store.totalValue() * 100) / 100;
    this.store.setModelPot(dollars);

    // ⚠️ Write the ACCEPTED figure back, for the same reason `onPot` does: `setModelPot` clamps,
    // and when the clamp lands on the pot the request already held, this expression returns the
    // string it returned last time. Angular sees no change, nothing re-renders, and the box goes
    // on showing a percentage the request does not hold.
    input.value = this.potPercentInput();
  }

  /**
   * What applying the remainder would move: `investable − pot`.
   *
   * ⚠️ Against the POT, not `unappliedValue`. The two agree whenever the target column totals
   * 100 -- which is every state the step can be left in -- and diverge only mid-edit, where the
   * rows split the pot into something other than the pot. It is the pot this control sets, so
   * this is the figure that is exactly closed by one click; keying the button on the unapplied
   * figure would leave it offering an amount it cannot fully place.
   */
  private remaining(): number {
    return this.store.investable() - this.store.modelPot();
  }

  /**
   * Whether the control has anything to do.
   *
   * ⚠️ NOT gated on the grid being sound, which its pro-rata predecessor had to be: that one
   * divided money across the rows, so it needed a model on every row and a target column on
   * 100, and was therefore unavailable exactly when a fresh grid needed funding. This one moves
   * the total and touches no row, so there is nothing about the split it could get wrong.
   */
  canApplyRemaining(): boolean {
    return Math.abs(this.remaining()) >= 0.005;
  }

  /**
   * What the control says.
   *
   * ⚠️ NO AMOUNT. It read `Apply Remaining $114,119.36`, and the figure made the button the
   * widest thing in the panel -- wider than the field it acts on, and re-flowing the row on every
   * keystroke as the remainder changed. The amount moved to the info icon beside it, where it can
   * be read on demand and cannot resize anything.
   *
   * Still names the DIRECTION, because the same control resolves an unplaced balance and an
   * over-commitment and those are opposite operations -- "Allocate Remaining" on a button that
   * was about to REDUCE the total would be a lie.
   */
  remainingLabel(): string {
    return this.remaining() > 0 ? 'Allocate Remaining' : 'Remove Excess';
  }

  /**
   * The sentence behind the info icon, and the button's own accessible name.
   *
   * ⚠️ This is where the amount lives now. It carries what the label used to: the figure being
   * moved, not the total it lands on -- the delta is what the advisor is already reading off Left
   * to allocate, and it is what makes the click predictable.
   *
   * Doubling as `aria-label` on the button is deliberate: a `title` on a decorative icon is
   * announced inconsistently, and a screen reader hearing only "Allocate Remaining" would be
   * told less than a sighted advisor who can hover.
   */
  remainingHint(): string {
    const remaining = this.remaining();
    const money = Math.abs(remaining).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return remaining > 0
      ? `Allocate remaining ${money} to total model amount.`
      : `Remove ${money} of excess from the total model amount.`;
  }

  /** True when the outstanding amount is an over-commitment rather than money still to place. */
  isOverAllocated(): boolean {
    return this.store.unappliedValue() < 0;
  }

  isOffTarget(sum: number): boolean {
    return Math.abs(sum - 100) >= 0.005;
  }

  /**
   * How far a percentage column is from 100, as a signed figure in parentheses.
   *
   * Signed rather than the words "over" / "under". Direction is carried by the sign and by
   * the colour (see {@link deltaClass}), and parentheses keep it reading as an annotation on
   * the total above rather than as a second, competing number. It also stays short enough
   * not to wrap under a narrow column, which the word form did.
   */
  deltaLabel(delta: number): string {
    return `(${this.sign(delta)}${Math.abs(delta).toFixed(2)}%)`;
  }

  /**
   * How far this sleeve has drifted from its stated target, in percentage points -- `+1.23%`
   * when it holds a bigger slice of the model money than its target claims, `(1.23%)` when it
   * holds a smaller one, and '' when there is nothing to say.
   *
   * ⚠️ THE ONLY statement of the drift now. A `driftLabel` / `isOverfunded` pair said the same
   * thing in dollars on the model name's reference line, and both are gone: it is this figure
   * multiplied by the pot, and an advisor restating a mix is working in percentages -- the
   * dollars are what the wizard computes for them. One fact, in the column whose question it
   * answers. {@link AllocationRow.sleeveDrift} still carries the dollar form if it is wanted
   * back; the markup for it is in git.
   *
   * ⚠️ A figure, not the words OVERFUNDED / UNDERFUNDED this pair used to read. The words needed
   * a pill to hold them and the pill needed a colour, and between them they drowned the figure
   * they were annotating. A signed delta says the same thing in a quarter of the space, and it
   * reads as what it is: a difference. The words survive in the `title`, which is also what
   * carries the meaning to a screen reader, since neither a sign nor a bracket speaks for itself.
   *
   * ⚠️ Standard financial convention, which is the only reason to pick one direction over the
   * other: PARENTHESES MEAN NEGATIVE. `actual share − stated target`, so an overfunded sleeve is
   * the positive figure and takes the `+`, and an underfunded one is the negative figure and
   * takes the brackets. Reversing that -- `+` for the shortfall, on the reading that `+` means
   * "add this much" -- was considered and rejected: it makes the sign describe the remedy while
   * the colour describes the state, and a reader who knows the convention would take a bracketed
   * figure for a surplus.
   *
   * Returning the empty string rather than null is deliberate: the template can then use
   * `@if (actualPctDeltaLabel(...); as gap)` and get the guard and the text from one call,
   * instead of testing one expression and rendering another that could drift from it.
   *
   * '' when either figure is missing -- an advisor-added row has neither -- or when the gap is
   * under half a basis point, where "+0.00%" would draw the eye to the one row that is fine.
   */
  actualPctDeltaLabel(actual: number | null | undefined, target: number | null | undefined): string {
    if (actual == null || target == null) return '';
    const delta = actual - target;
    if (Math.abs(delta) < 0.005) return '';
    return delta > 0 ? `+${delta.toFixed(2)}%` : `(${Math.abs(delta).toFixed(2)}%)`;
  }

  /**
   * Whether the sleeve's actual share EXCEEDS its stated target -- the colour and the wording.
   *
   * ⚠️ NOT `isOverAllocated`, which already exists on this component and means something else
   * entirely: that the REQUEST commits more than the account has. This is about one sleeve's
   * share of today's model money against its own target, and confusing the two would put the
   * over-commitment colour on a perfectly ordinary row.
   */
  holdsMoreThanTarget(actual: number | null | undefined, target: number | null | undefined): boolean {
    return actual != null && target != null && actual - target > 0;
  }

  /**
   * A true minus sign, not a hyphen — matching the − already used elsewhere in this wizard.
   *
   * Only called for deltas that {@link isOffTarget} / {@link isOffValue} have already found
   * non-zero, so there is no third case to render.
   */
  private sign(delta: number): string {
    return delta > 0 ? '+' : '−';
  }

  /**
   * Colour for the over/under line: red when over, mint when under.
   *
   * The two are not the same kind of problem. Over means the advisor has committed more
   * than exists and something must come back out. Under means there is simply more still
   * to place — a normal mid-edit state, not a mistake. Painting both red would flag the
   * ordinary act of typing as an error.
   *
   * `mint-darkest` rather than a lighter mint: on this near-white row the lighter tokens
   * do not carry enough contrast to read as text.
   */
  deltaClass(delta: number): string {
    return delta > 0 ? 'text-red-dark' : 'text-mint-darkest';
  }
}
